import { Loader2, XCircle } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { AuthLayout } from '@/components/layout/AuthLayout'
import { Card, CardContent } from '@/components/ui/card'
import { shopifyService } from '@/services/shopify'
import { useAuthStore } from '@/stores/auth'

type CallbackState = 'loading' | 'error'

export function ShopifyAuthCallbackPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [state, setState] = useState<CallbackState>('loading')
  const hasRunRef = useRef(false)

  const token = searchParams.get('token')

  useEffect(() => {
    if (hasRunRef.current) return
    hasRunRef.current = true

    if (!token) {
      navigate('/login?error=shopify_session_missing', { replace: true })
      return
    }

    ;(async () => {
      try {
        const response = await shopifyService.exchangeSession(token)

        useAuthStore.setState({
          user: response.user,
          organization: response.organization,
          role: response.role,
          tokens: {
            accessToken: response.accessToken,
            refreshToken: response.refreshToken,
            expiresAt: Date.now() + response.expiresIn * 1000,
          },
          authState: 'authenticated',
          error: null,
          isLoading: false,
        })

        const returnTo = response.returnTo
        if (
          returnTo &&
          returnTo.startsWith('/') &&
          !returnTo.startsWith('//')
        ) {
          navigate(returnTo, { replace: true })
        } else {
          navigate('/integrations?shopify=connected', { replace: true })
        }
      } catch {
        setState('error')
      }
    })()
  }, [token, navigate])

  if (state === 'error') {
    return (
      <AuthLayout title="Shopify sign-in failed">
        <Card>
          <CardContent className="flex flex-col items-center gap-4 py-8">
            <XCircle className="h-8 w-8 text-red-600" />
            <p className="text-sm text-muted-foreground text-center">
              We couldn&apos;t complete your Shopify sign-in. The link may have
              expired.
            </p>
            <button
              type="button"
              onClick={() => navigate('/login', { replace: true })}
              className="text-sm font-medium text-primary hover:underline"
            >
              Go to login
            </button>
          </CardContent>
        </Card>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout title="Connecting your Shopify store">
      <Card>
        <CardContent className="flex flex-col items-center gap-4 py-8">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">
            Finishing your Shopify install…
          </p>
        </CardContent>
      </Card>
    </AuthLayout>
  )
}
